import { useEffect, useState } from "react";
import { Button } from "primereact/button";
import { FiEdit, FiRefreshCw, FiSave, FiX } from "react-icons/fi";
import {
  StatusTable,
  TableHeader,
  ParamsCollapseHeader,
  ParamsCollapseTitle,
} from "styles/devices/deviceDetails.styles";
import {
  ButtonsContainer,
  DisableOverlay,
  ParamsHeaderActions,
  ParamsToolbarHint,
  StatusRefreshButton,
  TableContainer,
} from "styles/devices/deviceTable.styles";
import { StatusParametersTableProps } from "types/device.types";
import { useResponsive } from "hooks/useResponsive";
import { StatusParametersRows } from "./StatusParametersRows";

export function StatusParametersTable({
  shadow,
  isEditingParams,
  updatingParams,
  pendingParams,
  toggleParamEditMode,
  handleSaveParams,
  handleParamChange,
  handleToggleSensorConnection,
  editingParameter,
  handleCancelEdit,
  hasDirtyParams = false,
  dirtyCount = 0,
  onRefresh,
  isRefreshing = false,
}: StatusParametersTableProps) {
  const { isMobile } = useResponsive();
  const [paramsExpanded, setParamsExpanded] = useState(!isMobile);

  useEffect(() => {
    setParamsExpanded(!isMobile);
  }, [isMobile]);

  const showTable = !isMobile || paramsExpanded;

  const refreshButton = onRefresh ? (
    <StatusRefreshButton
      type="button"
      icon={<FiRefreshCw />}
      onClick={onRefresh}
      disabled={updatingParams || isRefreshing}
      className={isRefreshing ? "is-refreshing" : undefined}
      aria-label="Refresh device status"
      text
    />
  ) : null;

  return (
    <TableContainer $editing={isEditingParams}>
      {updatingParams && <DisableOverlay />}

      {isMobile ? (
        <ParamsCollapseHeader>
          <ParamsCollapseTitle>Parameters</ParamsCollapseTitle>
          <ParamsHeaderActions>
            <Button
              type="button"
              label={paramsExpanded ? "Hide" : "Show"}
              icon={paramsExpanded ? "pi pi-chevron-up" : "pi pi-chevron-down"}
              className="p-button-outlined p-button-sm"
              aria-expanded={paramsExpanded}
              aria-controls="status-parameters-table"
              onClick={() => setParamsExpanded((prev) => !prev)}
              style={{ minHeight: 44, minWidth: 44 }}
            />
            {refreshButton}
          </ParamsHeaderActions>
        </ParamsCollapseHeader>
      ) : null}

      {showTable && (
        <>
          <StatusTable id="status-parameters-table">
            <TableHeader>
              <tr>
                <th>Parameter</th>
                <th>Current Value</th>
                <th>Set Point</th>
                <th>Connection State</th>
              </tr>
            </TableHeader>
            <StatusParametersRows
              shadow={shadow}
              isEditingParams={isEditingParams}
              updatingParams={updatingParams}
              pendingParams={pendingParams}
              handleParamChange={handleParamChange}
              handleToggleSensorConnection={handleToggleSensorConnection}
              editingParameter={editingParameter}
            />
          </StatusTable>

          <ButtonsContainer>
            {!isMobile && refreshButton}
            {isEditingParams ? (
              <>
                <ParamsToolbarHint>
                  {hasDirtyParams
                    ? `${dirtyCount} unsaved change${dirtyCount === 1 ? "" : "s"} · Esc to cancel`
                    : "Esc to cancel"}
                </ParamsToolbarHint>
                <Button
                  type="button"
                  icon={<FiX />}
                  label="Cancel"
                  onClick={handleCancelEdit}
                  disabled={updatingParams}
                  text
                />
                <Button
                  type="button"
                  icon={<FiSave />}
                  label="Save"
                  onClick={handleSaveParams}
                  disabled={updatingParams || !hasDirtyParams}
                  loading={updatingParams}
                />
              </>
            ) : (
              <Button
                type="button"
                icon={<FiEdit />}
                label="Edit Parameters"
                onClick={toggleParamEditMode}
                disabled={updatingParams}
                outlined
              />
            )}
          </ButtonsContainer>
        </>
      )}
    </TableContainer>
  );
}
